import { useLocation, useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import {
  useTable,
  usePagination,
  useGlobalFilter,
  useSortBy,
  TableState,
} from 'react-table'
import { IoChevronForwardOutline, IoChevronBackOutline } from 'react-icons/io5'
import { BsChevronRight } from 'react-icons/bs'
import TableArrowBtn from '../UI/TableArrowBtn'
import Spinner from '../UI/Spinner'
import Search from './Search'
import SortableHeader, { SortableColumn } from './SortableHeader'
import { useReportsContext } from '../../context/reports/reports'
import { hasBranches } from '../../helpers/branches'
import { useUsersContext } from '../../context/users/users'
import { ReactNode, useEffect } from 'react'

interface Props {
  data: any
  columns: any
  showSearch?: boolean
  noDataFoundMessage?: string
  isDataLoading?: boolean
  showLastColumn?: boolean
  tableTitleElement?: ReactNode
  pageSize?: number
  tableHeightClassName?: string
  /** Opt-in: only the two super admin lists sort. */
  isSortable?: boolean
  /** Sort applied on mount, e.g. `[{ id: 'id', desc: true }]`. */
  defaultSortBy?: { id: string; desc?: boolean }[]
}

// Module scope so the default is a stable reference rather than a new array
// on every render.
const NO_SORT: { id: string; desc?: boolean }[] = []

const TableItems = ({
  columns,
  data = [],
  showSearch = true,
  showLastColumn = true,
  noDataFoundMessage,
  isDataLoading,
  tableTitleElement,
  pageSize = 5,
  tableHeightClassName = 'h-[585px]',
  isSortable = false,
  defaultSortBy = NO_SORT,
}: Props) => {
  const { t } = useTranslation()
  const { pathname } = useLocation()
  const navigate = useNavigate()
  const { isReportLoading, selectedOrg } = useReportsContext()
  const { isUserLoading } = useUsersContext()
  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    page,
    nextPage,
    previousPage,
    canNextPage,
    canPreviousPage,
    pageOptions,
    state,
    rows,
    prepareRow,
    setGlobalFilter,
    setPageSize,
  } = useTable(
    {
      columns,
      data,
      // @types/react-table only describes useSortBy's state behind a module
      // augmentation this project does not set up, so `sortBy` is cast in.
      initialState: { pageSize, sortBy: defaultSortBy } as Partial<
        TableState<object>
      >,
      // Off unless a caller asks: this table also draws Reports, Users,
      // Invoices and an organisation's own users, none of which sort.
      disableSortBy: !isSortable,
      // Both sorting pages filter in a useMemo, so `data` is a fresh array on
      // every keystroke. Left on, this would drop the sort as the user types.
      autoResetSortBy: false,
    },
    useGlobalFilter,
    useSortBy,
    usePagination,
  )

  const { pageSize: currentPageSize, pageIndex, globalFilter } = state
  const isReportsRoute = pathname === '/compliance-officer/reports'

  // initialState only applies on mount, so a caller that changes pageSize
  // later (the expandable users table) needs it pushed through explicitly.
  useEffect(() => {
    setPageSize(pageSize)
  }, [pageSize, setPageSize])

  // The reports/users context flags describe other pages' fetches, so a caller
  // that owns its own request passes isDataLoading and that wins outright.
  const isLoading =
    isDataLoading !== undefined
      ? isDataLoading
      : isReportsRoute
        ? isReportLoading
        : isUserLoading

  const branchClasses = showLastColumn
    ? hasBranches(selectedOrg) && isReportsRoute
      ? 'first:w-[8%] w-[10%] last:text-black text-left'
      : 'first:w-[8%] w-1/5 last:text-transparent text-center'
    : ''
  return (
    <>
      {showSearch && (
        <Search filter={globalFilter} setFilter={setGlobalFilter} />
      )}
      {tableTitleElement && (
        <div className='flex items-center justify-between gap-5 flex-wrap mb-7'>
          {tableTitleElement}
          <Search
            filter={globalFilter}
            setFilter={setGlobalFilter}
            className='static top-auto right-auto md:right-auto'
          />
        </div>
      )}
      <div
        className={`${tableHeightClassName} relative overflow-x-auto hide-scrollbar flex transition-[height] duration-300`}
      >
        {isLoading ? (
          <Spinner spinner='table' width='w-11' height='h-11' color='#0C75FF' />
        ) : rows.length === 0 ? (
          <div className='flex justify-center items-center w-full'>
            <p className='text-2xl text-lightGray'>
              {noDataFoundMessage
                ? t(noDataFoundMessage)
                : isReportsRoute
                  ? t('noReportsFound')
                  : t('noUsersFound')}
            </p>
          </div>
        ) : (
          <table
            className='w-full border-spacing border-separate absolute min-w-[1100px]'
            {...getTableProps()}
          >
            <thead>
              {headerGroups.map((headerGroup) => {
                const { key: headerGroupKey, ...headerGroupProps } =
                  headerGroup.getHeaderGroupProps()
                return (
                  <tr key={headerGroupKey} {...headerGroupProps}>
                    {headerGroup.headers.map((column) => {
                      const { key: columnKey, ...columnProps } =
                        column.getHeaderProps()
                      // useSortBy's additions are not in the base column type,
                      // and this table's columns come in untyped anyway.
                      const sortable = column as unknown as SortableColumn & {
                        canSort: boolean
                      }
                      return (
                        <th
                          key={columnKey}
                          className={`first:text-left first:pl-9 text-base font-bold ${branchClasses}`}
                          {...columnProps}
                          aria-sort={
                            sortable.isSorted
                              ? sortable.isSortedDesc
                                ? 'descending'
                                : 'ascending'
                              : undefined
                          }
                        >
                          {sortable.canSort ? (
                            <SortableHeader column={sortable} />
                          ) : (
                            column.render('Header')
                          )}
                        </th>
                      )
                    })}
                  </tr>
                )
              })}
            </thead>
            <tbody {...getTableBodyProps()}>
              {page.map((row) => {
                prepareRow(row)
                const { original }: any = row
                const { key: rowKey, ...rowProps } = row.getRowProps()
                return (
                  <tr
                    key={rowKey}
                    {...rowProps}
                    className={`${
                      isReportsRoute
                        ? 'hover:bg-[#FCFCFF] transition-all cursor-pointer group'
                        : ''
                    }  `}
                    onClick={() =>
                      isReportsRoute ? navigate(`report/${original.id}`) : null
                    }
                  >
                    {row.cells.map((cell, idx) => {
                      const { key: cellKey, ...cellProps } = cell.getCellProps()
                      return (
                        <td
                          key={cellKey}
                          className={`first:text-left first:pl-9 pr-2 first:border-l first:rounded-l-lg  last:border-r last:rounded-r-lg border-y-[1px] border-solid border-borderGray text-lightGray text-base font-normal h-[5rem] ${
                            isReportsRoute && hasBranches(selectedOrg)
                              ? 'last:pr-0 text-left'
                              : 'last:pr-8 text-center'
                          } `}
                          {...cellProps}
                        >
                          {cell.render('Cell')}
                          {idx === 5 &&
                            isReportsRoute &&
                            !hasBranches(selectedOrg) && (
                              <BsChevronRight className='ml-auto text-lg group-hover:opacity-100 opacity-0 transition-all' />
                            )}
                        </td>
                      )
                    })}
                  </tr>
                )
              })}
            </tbody>
          </table>
        )}
      </div>
      <div className='border-t-[1px] border-solid border-borderGray flex justify-between items-center pt-5 flex-wrap gap-4'>
        <div>
          {rows.length !== 0 && (
            <p className='text-sm'>
              {t('showing')} {currentPageSize * pageIndex + 1} {t('to')}{' '}
              {currentPageSize * pageIndex + 1 + page.length - 1} {t('of')}{' '}
              {rows.length} {t('entries')}
            </p>
          )}
        </div>
        {rows.length !== 0 && (
          <div className='flex justify-between items-center gap-7'>
            <TableArrowBtn onClick={previousPage} disabled={!canPreviousPage}>
              <IoChevronBackOutline className='text-lg' />
            </TableArrowBtn>
            <p className='text-sm'>
              <span className='text-primaryBlue'>{pageIndex + 1}</span>{' '}
              {t('of')} {pageOptions.length} {t('pages')}
            </p>
            <TableArrowBtn onClick={nextPage} disabled={!canNextPage}>
              <IoChevronForwardOutline className='text-lg' />
            </TableArrowBtn>
          </div>
        )}
      </div>
    </>
  )
}

export default TableItems
